<template>
  <div class="mt-3">
    <div class="flat-card">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h6 class="mb-0" style="font-weight:600;color:#0f172a;">Audit Log</h6>
          <small class="text-secondary">Last 50 admin actions (24-hour rolling window)</small>
        </div>
        <button class="btn btn-outline-secondary" @click="fetch" :disabled="loading">
          <i class="bi bi-arrow-clockwise" :class="{ spin: loading }"></i>
        </button>
      </div>

      <div v-if="loading" class="text-center py-4">
        <div class="spinner-border text-secondary" role="status"></div>
      </div>

      <div v-else-if="log.length === 0" class="text-center text-secondary py-4">
        <i class="bi bi-journal-text" style="font-size:2rem;"></i>
        <p class="mt-2 mb-0"><small>No activity recorded yet</small></p>
      </div>

      <div v-else>
        <!-- Filter -->
        <div class="mb-3">
          <input
            v-model="search"
            class="form-control"
            placeholder="Filter by actor, action, or company…"
          />
        </div>

        <div class="timeline">
          <div
            v-for="(entry, i) in filtered"
            :key="i"
            class="timeline-item d-flex mb-3"
          >
            <!-- Icon dot -->
            <div class="timeline-dot mr-3 flex-shrink-0">
              <div
                class="rounded-circle d-flex align-items-center justify-content-center"
                :class="dotClass(entry.type)"
                style="width:32px;height:32px;"
              >
                <i :class="['bi', dotIcon(entry.type)]" style="font-size:.75rem;"></i>
              </div>
            </div>

            <!-- Content -->
            <div class="flex-grow-1 border-bottom pb-2" style="min-width:0;">
              <div class="d-flex justify-content-between align-items-start">
                <div style="min-width:0;">
                  <span class="font-weight-bold" style="font-size:.85rem;">{{ formatType(entry.type) }}</span>
                  <span class="text-secondary ml-2" style="font-size:.75rem;">by {{ entry.actor }}</span>
                </div>
                <small class="text-secondary flex-shrink-0 ml-2" style="font-size:.72rem;">{{ fromNow(entry.timestamp) }}</small>
              </div>
              <div v-if="entry.payload && hasPayloadDetails(entry.payload)" class="mt-1">
                <small class="text-secondary" style="font-size:.75rem;">
                  <span v-if="entry.payload.company_name" class="font-weight-bold text-dark">{{ entry.payload.company_name }}</span>
                  <span v-if="entry.payload.days"> · {{ entry.payload.days }} days</span>
                  <span v-if="entry.payload.message"> · "{{ truncate(entry.payload.message, 60) }}"</span>
                  <span v-if="entry.payload.role"> · {{ entry.payload.role }}</span>
                  <span v-if="entry.payload.flag"> · flag: {{ entry.payload.flag }}</span>
                  <span v-if="entry.payload.count !== undefined"> · {{ entry.payload.count }} jobs</span>
                </small>
              </div>
            </div>
          </div>
        </div>

        <div v-if="filtered.length === 0" class="text-center text-secondary py-2">
          <small>No entries match your filter</small>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AdminAuditLog',

  data() {
    return {
      loading: false,
      log: [],
      search: '',
    };
  },

  computed: {
    filtered() {
      if (!this.search.trim()) return this.log;
      const q = this.search.toLowerCase();
      return this.log.filter(e =>
        (e.actor || '').toLowerCase().includes(q) ||
        (e.type || '').toLowerCase().includes(q) ||
        JSON.stringify(e.payload || {}).toLowerCase().includes(q)
      );
    },
  },

  mounted() {
    this.fetch();
  },

  methods: {
    async fetch() {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/admin/activity');
        this.log = data;
      } finally {
        this.loading = false;
      }
    },

    formatType(type) {
      return type
        .replace(/\./g, ' › ')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
    },

    dotClass(type) {
      if (type.includes('delete') || type.includes('suspend') || type.includes('archive')) return 'bg-danger text-white';
      if (type.includes('billing') || type.includes('trial') || type.includes('waive')) return 'bg-dark text-white';
      if (type.includes('staff') || type.includes('flag') || type.includes('maintenance')) return 'bg-primary text-white';
      if (type.includes('note') || type.includes('notif') || type.includes('announcement')) return 'bg-dark text-white';
      return 'bg-secondary text-white';
    },

    dotIcon(type) {
      if (type.includes('delete') || type.includes('suspend')) return 'bi-x-circle';
      if (type.includes('archive')) return 'bi-archive';
      if (type.includes('billing') || type.includes('waive')) return 'bi-credit-card';
      if (type.includes('trial') || type.includes('extend')) return 'bi-hourglass-split';
      if (type.includes('staff')) return 'bi-person-badge';
      if (type.includes('flag')) return 'bi-toggles';
      if (type.includes('note')) return 'bi-sticky';
      if (type.includes('notif') || type.includes('announcement')) return 'bi-megaphone';
      if (type.includes('jobs')) return 'bi-arrow-repeat';
      return 'bi-activity';
    },

    hasPayloadDetails(payload) {
      return payload.company_name || payload.days || payload.message || payload.role || payload.flag || payload.count !== undefined;
    },

    truncate(str, len) {
      return str && str.length > len ? str.slice(0, len) + '…' : str;
    },

    fromNow(ts) {
      return moment(ts).fromNow();
    },
  },
};
</script>

<style scoped>
.flat-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 14px;
  padding: 20px;
}
.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
.timeline-dot {
  padding-top: 2px;
}
</style>
